import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import awsLogoLight from '@/assets/icons/aws.svg';
import awsLogoDark from '@/assets/icons/aws-dark.svg';
import ociLogo from '@/assets/icons/oci.png';
import {
  apiClient,
  InventoryCluster,
  EnhancedResource,
  TopologyRiskFinding,
  TopologyIncident,
  CloudResource,
  CloudResourcesSummary,
  CloudIncidentSummary,
  AWSConnection,
  OCIConnection,
  OCIResourcesSummary
} from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getStatusLabel } from '@/lib/incidents';
import { resolveStatus, getStatusDotClass, getConnectionLabel } from '@/utils/clusterHealth';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Loader2,
  Search,
  Package,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  RefreshCw,
  Cloud,
  Server,
  Database,
  Key,
  Shield,
  HardDrive,
  Network,
  Users,
  Lock,
  AlertTriangle,
  ShipWheel,
  Boxes
} from 'lucide-react';
import KubernetesIcon, { KubernetesIconType } from '../components/KubernetesIcon';
import { cn } from '@/lib/utils';
// Using native date formatting to avoid dependency issues
// import { format } from 'date-fns';
import { Alert, AlertDescription } from '@/components/ui/alert';

// Main Inventory component with tabs for Kubernetes and Cloud
export default function Inventory() {
  const [activeTab, setActiveTab] = useState<'kubernetes' | 'cloud'>('kubernetes');

  return (
    <div className="space-y-6">
      {/* Top-level tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'kubernetes' | 'cloud')} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="kubernetes" className="flex items-center gap-2">
            <ShipWheel className="h-4 w-4" />
            Kubernetes
          </TabsTrigger>
          <TabsTrigger value="cloud" className="flex items-center gap-2">
            <Cloud className="h-4 w-4" />
            Cloud
          </TabsTrigger>
        </TabsList>

        <TabsContent value="kubernetes" className="mt-6">
          <KubernetesInventoryTab />
        </TabsContent>

        <TabsContent value="cloud" className="mt-6">
          <CloudInventoryTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Kubernetes Inventory Tab Component
function KubernetesInventoryTab() {
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [resourceType, setResourceType] = useState<'all' | 'cluster' | 'workload' | 'service'>('all');
  const [selectedCluster, setSelectedCluster] = useState<string>('all');
  const [selectedNamespace, setSelectedNamespace] = useState<string>('all');
  const [namespaces, setNamespaces] = useState<Array<{id: string; name: string}>>([]);
  const [resources, setResources] = useState<EnhancedResource[]>([]);
  const [clusters, setClusters] = useState<InventoryCluster[]>([]);
  const [clustersLoaded, setClustersLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [uniqueKindsCount, setUniqueKindsCount] = useState(0);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [apiSuggestions, setApiSuggestions] = useState<string[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showNoResults, setShowNoResults] = useState(false);
  const [selectedResource, setSelectedResource] = useState<EnhancedResource | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Incident correlation state - maps resource key to incident info
  const [incidentMap, setIncidentMap] = useState<Map<string, {
    incidentCount: number;
    highestSeverity: 'critical' | 'high' | 'medium' | 'low';
    hasRecentIncident: boolean;
    latestIncidentAt: number;
  }>>(new Map());

  // Use ref for cache to avoid re-render dependency cycles
  const suggestionCacheRef = useRef<Map<string, { suggestions: string[], timestamp: number }>>(new Map());

  // Debounce search query for main search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1); // Reset to first page on search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Memoized function to fetch suggestions (prevents recreation on every render)
  const fetchSuggestions = useCallback(async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setApiSuggestions([]);
      setLoadingSuggestions(false);
      setShowNoResults(false);
      return;
    }

    // Create cache key with filters to ensure context-aware caching
    const cacheKey = `${query.toLowerCase()}:${resourceType}:${selectedCluster}:${selectedNamespace}`;

    // Check cache first using ref (no re-render dependency)
    const cache = suggestionCacheRef.current;
    const cached = cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < 5 * 60 * 1000) {
      setApiSuggestions(cached.suggestions);

      // Handle no results state for cached results
      if (cached.suggestions.length === 0) {
        setTimeout(() => {
          setShowNoResults(true);
        }, 500);
      } else {
        setShowNoResults(false);
      }
      return;
    }

    setLoadingSuggestions(true);
    setShowNoResults(false); // Hide no results while loading

    try {
      const response = await apiClient.getSearchSuggestions(query, {
        type: resourceType !== 'all' ? resourceType : undefined,
        cluster_id: selectedCluster !== 'all' ? selectedCluster : undefined,
        namespace: selectedNamespace !== 'all' ? selectedNamespace : undefined,
      });

      // Set suggestions - empty arrays are valid and will show "No suggestions found"
      setApiSuggestions(response.suggestions);

      // Show "No results" message after a brief delay if no suggestions
      if (response.suggestions.length === 0) {
        setTimeout(() => {
          setShowNoResults(true);
        }, 500); // Half second delay before showing "No suggestions found"
      } else {
        setShowNoResults(false);
      }

      // Update cache using ref
      cache.set(cacheKey, {
        suggestions: response.suggestions,
        timestamp: Date.now()
      });

      // Limit cache size to 50 entries
      if (cache.size > 50) {
        const firstKey = cache.keys().next().value;
        if (firstKey) {
          cache.delete(firstKey);
        }
      }
    } catch (err) {
      console.error('Failed to fetch search suggestions:', err);
      setApiSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [resourceType, selectedCluster, selectedNamespace]);

  // Debounced API call for search suggestions
  useEffect(() => {
    // Clear no results state immediately when user starts typing
    setShowNoResults(false);

    const timer = setTimeout(() => {
      fetchSuggestions(searchQuery);
    }, 300); // 300ms debounce for suggestions (faster than main search)

    return () => clearTimeout(timer);
  }, [searchQuery, fetchSuggestions]);

  // Fetch clusters for the filter dropdown
  useEffect(() => {
    const fetchClusters = async () => {
      try {
        const clustersResponse = await apiClient.getInventoryClusters();
        setClusters(clustersResponse.clusters);
      } catch (err) {
        console.error('Failed to fetch clusters:', err);
      } finally {
        setClustersLoaded(true);
      }
    };
    fetchClusters();
  }, []);

  // Fetch namespaces when cluster changes
  useEffect(() => {
    const fetchNamespaces = async () => {
      if (selectedCluster === 'all') {
        // Fetch all namespaces from all clusters
        try {
          const allNamespaces: Array<{id: string; name: string}> = [];
          const nameSet = new Set<string>();

          for (const cluster of clusters) {
            const nsResponse = await apiClient.getInventoryNamespaces(cluster.uid || cluster.id);
            nsResponse.namespaces.forEach(ns => {
              if (!nameSet.has(ns.name)) {
                nameSet.add(ns.name);
                allNamespaces.push({ id: ns.id, name: ns.name });
              }
            });
          }

          setNamespaces(allNamespaces.sort((a, b) => a.name.localeCompare(b.name)));
        } catch (err) {
          console.error('Failed to fetch namespaces:', err);
          setNamespaces([]);
        }
      } else {
        // Fetch namespaces for selected cluster
        try {
          const cluster = clusters.find(c => c.id === selectedCluster);
          if (cluster) {
            const nsResponse = await apiClient.getInventoryNamespaces(cluster.uid || cluster.id);
            setNamespaces(nsResponse.namespaces.map(ns => ({ id: ns.id, name: ns.name })));
          }
        } catch (err) {
          console.error('Failed to fetch namespaces:', err);
          setNamespaces([]);
        }
      }
    };

    if (clusters.length > 0) {
      fetchNamespaces();
    }
  }, [selectedCluster, clusters]);

  // Fetch enhanced inventory data
  const fetchInventoryData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.getEnhancedInventory({
        type: resourceType,
        cluster_id: selectedCluster === 'all' ? undefined : selectedCluster,
        namespace: selectedNamespace === 'all' ? undefined : selectedNamespace,
        include_risks: true,
        include_incidents: true,
        page: currentPage,
        page_size: itemsPerPage,
        search: debouncedSearchQuery || undefined
      });

      setResources(response.resources || []);
      setTotalItems(response.total_count || 0);
      setTotalPages(response.total_pages || 0);
      if (response.unique_kinds_count !== undefined) {
        setUniqueKindsCount(response.unique_kinds_count);
      }
    } catch (err: any) {
      console.error('Failed to fetch inventory data:', err);
      setError(err.response?.data?.message || 'Failed to fetch inventory data');
      setResources([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [resourceType, selectedCluster, selectedNamespace, currentPage, itemsPerPage, debouncedSearchQuery]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchInventoryData();
  }, [fetchInventoryData]);

  // Extract incident info from dedicated fields - no separate API calls needed
  // The enhanced inventory endpoint includes incident_count and incident_severity fields
  useEffect(() => {
    if (!resources || resources.length === 0) {
      setIncidentMap(new Map());
      return;
    }

    const newIncidentMap = new Map<string, {
      incidentCount: number;
      highestSeverity: 'critical' | 'high' | 'medium' | 'low';
      hasRecentIncident: boolean;
      latestIncidentAt: number;
    }>();

    // Extract incident info from dedicated fields for each resource
    for (const resource of resources) {
      if (resource.incident_count > 0) {
        const resourceKey = `${resource.type}:${resource.cluster_id || resource.uid}:${resource.namespace || ''}:${resource.name}`;
        const incidentData = {
          incidentCount: resource.incident_count,
          highestSeverity: (resource.incident_severity || 'medium') as 'critical' | 'high' | 'medium' | 'low',
          hasRecentIncident: true, // Active incidents from backend
          latestIncidentAt: Date.now() / 1000 // Not tracked per resource
        };
        // Set the resource-specific key
        newIncidentMap.set(resourceKey, incidentData);

        // Also set a cluster-level key for cluster resources so getResourceIncidentInfo
        // can find cluster-level incidents when doing fallback lookups
        if (resource.type === 'cluster') {
          const clusterKey = `cluster:${resource.cluster_id || resource.uid}`;
          newIncidentMap.set(clusterKey, incidentData);
        }
      }
    }

    setIncidentMap(newIncidentMap);
  }, [resources]);

  // Helper to get incident info for a K8s resource
  const getResourceIncidentInfo = (resource: EnhancedResource) => {
    // For cluster resources, use the cluster-specific key
    if (resource.type === 'cluster') {
      const clusterKey = `cluster:${resource.cluster_id || resource.uid}`;
      return incidentMap.get(clusterKey) || null;
    }

    // For workloads/services, only check resource-specific incidents (no cluster fallback)
    const resourceKey = `${resource.type}:${resource.cluster_id}:${resource.namespace}:${resource.name}`;
    return incidentMap.get(resourceKey) || null;
  };

  const getResourceIcon = (type: EnhancedResource['type'], kind?: string) => {
    switch (type) {
      case 'cluster':
        return <KubernetesIcon type="cluster" size={16} className="text-blue-600" />;
      case 'workload':
        // Map specific workload kinds to their custom Kubernetes icons
        const workloadKind = kind?.toLowerCase();
        let iconType: KubernetesIconType;

        switch (workloadKind) {
          case 'deployment':
            iconType = 'deployment';
            break;
          case 'statefulset':
            iconType = 'statefulset';
            break;
          case 'daemonset':
            iconType = 'daemonset';
            break;
          case 'replicaset':
            iconType = 'replicaset';
            break;
          case 'job':
            iconType = 'job';
            break;
          case 'cronjob':
            iconType = 'cronjob';
            break;
          case 'pod':
            iconType = 'pod';
            break;
          default:
            iconType = 'application';
        }
        return <KubernetesIcon type={iconType} size={16} className="text-purple-600" />;
      case 'service':
        return <KubernetesIcon type="service" size={16} className="text-green-600" />;
      default:
        return <Package className="h-4 w-4" />;
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleResourceTypeChange = (value: any) => {
    setResourceType(value);
    setCurrentPage(1);
  };

  const handleClusterChange = (value: string) => {
    setSelectedCluster(value);
    setCurrentPage(1);
  };

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return '-';
    // Convert Unix timestamp to Date and format using native methods
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // No kubernetes clusters connected - show setup prompt
  if (!loading && clustersLoaded && clusters.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <h3 className="text-lg font-semibold">No Kubernetes Clusters</h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
              Connect a Kubernetes cluster to start ingesting cluster resources.
              Go to the Kubernetes integration to set up a connection with the Kestrel operator.
            </p>
            <Button
              className="mt-4"
              onClick={() => window.location.href = '/onboard-cluster'}
            >
              Set Up Kubernetes Connection
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Kubernetes Resource Inventory</CardTitle>
            <Button onClick={fetchInventoryData} variant="outline" disabled={loading}>
              <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
              Refresh
            </Button>
          </div>
        </CardHeader>
          <CardContent className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{totalItems}</div>
                <p className="text-xs text-muted-foreground">Total Resources</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{uniqueKindsCount}</div>
                <p className="text-xs text-muted-foreground">Resource Types</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{clusters.length}</div>
                <p className="text-xs text-muted-foreground">Clusters</p>
              </CardContent>
            </Card>
          </div>
          </CardContent>
          <CardContent className="space-y-4">
            {/* Filters */}
            <div className="flex gap-4 flex-wrap">
              <div className="flex-1 min-w-[300px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search by name, namespace, or cluster..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    className="pl-9"
                  />
                  {/* Search Suggestions */}
                  {showSuggestions && searchQuery.length >= 2 && (loadingSuggestions || apiSuggestions.length > 0) && (
                    <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-background border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {loadingSuggestions && (
                        <div className="px-3 py-2 text-sm text-muted-foreground flex items-center gap-2">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Searching...
                        </div>
                      )}
                      {apiSuggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          className="w-full text-left px-3 py-2 hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground outline-none text-sm"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setSearchQuery(suggestion);
                            setShowSuggestions(false);
                          }}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* No results message - shown only after delay when no suggestions */}
                  {showSuggestions && searchQuery.length >= 2 && showNoResults && (
                    <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-background border border-border rounded-md shadow-lg">
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        No suggestions found
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Select value={resourceType} onValueChange={handleResourceTypeChange}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Resource Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Resources</SelectItem>
                  <SelectItem value="cluster">Clusters</SelectItem>
                  <SelectItem value="workload">Workloads</SelectItem>
                  <SelectItem value="service">Services</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedCluster} onValueChange={handleClusterChange}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select Cluster" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clusters</SelectItem>
                  {clusters.map(cluster => (
                    <SelectItem key={cluster.id} value={cluster.id}>
                      {cluster.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedNamespace} onValueChange={(value) => {
                setSelectedNamespace(value);
                setCurrentPage(1);
              }}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select Namespace" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Namespaces</SelectItem>
                  {namespaces.map(ns => (
                    <SelectItem key={ns.id} value={ns.name}>
                      {ns.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={itemsPerPage.toString()} onValueChange={(value) => {
                setItemsPerPage(parseInt(value));
                setCurrentPage(1);
              }}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="20">20 per page</SelectItem>
                  <SelectItem value="50">50 per page</SelectItem>
                  <SelectItem value="100">100 per page</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Error Alert */}
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Results Summary */}
            {!loading && !error && (
              <div className="text-sm text-muted-foreground">
                Showing {resources?.length || 0} of {totalItems} resources
                {debouncedSearchQuery && ` matching "${debouncedSearchQuery}"`}
                {" "}(Page {currentPage} of {totalPages})
              </div>
            )}

            {/* Table */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : (
              <>
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Namespace</TableHead>
                        <TableHead>Cluster</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(resources?.length || 0) === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                            {error ? 'Failed to load resources' : 'No resources found matching your filters'}
                          </TableCell>
                        </TableRow>
                      ) : (
                        (resources || []).map((resource) => {
                          const incidentInfo = getResourceIncidentInfo(resource);
                          return (
                          <TableRow
                            key={`${resource.type}-${resource.id}`}
                            className={cn(
                              "cursor-pointer hover:bg-muted/50",
                              incidentInfo?.hasRecentIncident && "animate-incident-pulse"
                            )}
                            title={incidentInfo?.hasRecentIncident
                              ? `⚠️ Active incident detected in the last 4 hours (${incidentInfo.incidentCount} incident${incidentInfo.incidentCount !== 1 ? 's' : ''}, ${incidentInfo.highestSeverity} severity). Click to view details.`
                              : undefined
                            }
                            onClick={() => {
                              setSelectedResource(resource);
                              setDetailsOpen(true);
                            }}
                          >
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getResourceIcon(resource.type, resource.kind)}
                                <span className="text-sm capitalize">
                                  {resource.kind || resource.type}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                {resource.name}
                                {incidentInfo?.hasRecentIncident && (
                                  <span
                                    className="relative flex h-2.5 w-2.5"
                                    title={`${incidentInfo.incidentCount} active incident(s) - ${incidentInfo.highestSeverity} severity`}
                                  >
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                                  </span>
                                )}
                                {resource.type === 'cluster' && (
                                  <div
                                    className={`w-2 h-2 rounded-full ${getStatusDotClass(resolveStatus(resource.connection_status, resource.is_connected))}`}
                                    title={getConnectionLabel(resolveStatus(resource.connection_status, resource.is_connected))}
                                  />
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {resource.namespace || '-'}
                            </TableCell>
                            <TableCell>
                              {resource.cluster_name || '-'}
                            </TableCell>
                            <TableCell>
                              {formatDate(resource.created_at)}
                            </TableCell>
                          </TableRow>
                        );})
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalItems > 0 && (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                    <div className="text-sm text-muted-foreground">
                      Page {currentPage} of {totalPages} ({totalItems} total items)
                    </div>
                    {totalPages > 1 && (
                      <div className="flex flex-wrap gap-2 items-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </Button>

                      {/* Page numbers - hidden on mobile to prevent overflow */}
                      <div className="hidden sm:flex gap-1">
                        {currentPage > 2 && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePageChange(1)}
                            >
                              1
                            </Button>
                            {currentPage > 3 && <span className="px-2 py-1">...</span>}
                          </>
                        )}

                        {currentPage > 1 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage - 1)}
                          >
                            {currentPage - 1}
                          </Button>
                        )}

                        <Button size="sm" disabled className="bg-foreground text-background hover:bg-foreground disabled:opacity-100">
                          {currentPage}
                        </Button>

                        {currentPage < totalPages && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage + 1)}
                          >
                            {currentPage + 1}
                          </Button>
                        )}

                        {currentPage < totalPages - 1 && (
                          <>
                            {currentPage < totalPages - 2 && <span className="px-2 py-1">...</span>}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePageChange(totalPages)}
                            >
                              {totalPages}
                            </Button>
                          </>
                        )}
                      </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
    </div>

    {/* Resource Details Dialog */}
    <ResourceDetailsDialog
      resource={selectedResource}
      open={detailsOpen}
      onOpenChange={setDetailsOpen}
    />
    </>
  );
}

// Resource Details Dialog Component
interface ResourceDetailsDialogProps {
  resource: EnhancedResource | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function ResourceDetailsDialog({ resource, open, onOpenChange }: ResourceDetailsDialogProps) {
  const navigate = useNavigate();
  const [findings, setFindings] = useState<TopologyRiskFinding[]>([]);
  const [isLoadingFindings, setIsLoadingFindings] = useState(false);
  const [findingsError, setFindingsError] = useState<string | null>(null);

  // Incident state
  const [incidents, setIncidents] = useState<TopologyIncident[]>([]);
  const [isLoadingIncidents, setIsLoadingIncidents] = useState(false);
  const [incidentsError, setIncidentsError] = useState<string | null>(null);

  // Fetch incidents when resource changes
  useEffect(() => {
    if (!resource || !open) {
      setIncidents([]);
      setIncidentsError(null);
      return;
    }

    const fetchIncidents = async () => {
      setIsLoadingIncidents(true);
      setIncidentsError(null);

      try {
        let params: {
          cluster_id: string;
          resource_type: 'workload' | 'namespace' | 'cluster';
          namespace?: string;
          workload?: string;
        };

        if (resource.type === 'workload') {
          params = {
            cluster_id: resource.cluster_id || '',
            resource_type: 'workload',
            namespace: resource.namespace || '',
            workload: resource.name,
          };
        } else if (resource.type === 'service') {
          params = {
            cluster_id: resource.cluster_id || '',
            resource_type: 'workload',
            namespace: resource.namespace || '',
            workload: resource.name,
          };
        } else if (resource.type === 'cluster') {
          params = {
            cluster_id: resource.uid || resource.cluster_id || '',
            resource_type: 'cluster',
          };
        } else {
          // Note: Namespace resources are only shown in Topology Map, not Inventory
          setIncidentsError('Incidents not available for this resource type');
          setIsLoadingIncidents(false);
          return;
        }

        const response = await apiClient.getIncidentsByResource(params);
        setIncidents(response.incidents || []);
      } catch (error: any) {
        console.error('Error fetching incidents:', error);
        setIncidentsError('Failed to load incidents');
      } finally {
        setIsLoadingIncidents(false);
      }
    };

    fetchIncidents();
  }, [resource, open]);

  /* Risk findings fetching — hidden while risks feature is disabled
  // Fetch risk findings when resource changes
  useEffect(() => {
    if (!resource || !open) {
      setFindings([]);
      setFindingsError(null);
      return;
    }

    const fetchRiskFindings = async () => {
      setIsLoadingFindings(true);
      setFindingsError(null);

      try {
        let requestData;

        if (resource.type === 'workload') {
          requestData = {
            cluster_id: resource.cluster_id || '',
            resource_type: 'workload' as const,
            namespace: resource.namespace || '',
            workload: resource.name,
            resource_kind: resource.kind
          };
        } else if (resource.type === 'service') {
          requestData = {
            cluster_id: resource.cluster_id || '',
            resource_type: 'workload' as const, // Query as workload type to get service-specific risks
            namespace: resource.namespace || '',
            workload: resource.name,
            resource_kind: 'Service'
          };
        } else if (resource.type === 'cluster') {
          requestData = {
            cluster_id: resource.uid || resource.id || resource.cluster_id || '',
            resource_type: 'cluster' as const
          };
        } else {
          setFindingsError('Risk findings not available for this resource type');
          setIsLoadingFindings(false);
          return;
        }

        const response = await apiClient.getRiskFindingsByResource(requestData);
        const fetchedFindings = response.findings;
        setFindings(fetchedFindings);
      } catch (error: any) {
        console.error('Error fetching risk findings:', error);
        setFindingsError('Failed to load risk findings');
      } finally {
        setIsLoadingFindings(false);
      }
    };

    fetchRiskFindings();
  }, [resource, open]);
  */

  if (!resource) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[650px] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {resource?.type === 'cluster' ? 'Cluster' :
             resource?.type === 'workload' ? 'Workload' :
             'Service'} Details: {resource?.name}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-hidden">
          <Tabs defaultValue="details" className="w-full h-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="incidents" className="flex items-center justify-center gap-2">
                <span>Incidents</span>
                {!isLoadingIncidents && incidents && incidents.length > 0 && (
                  <Badge variant="destructive" className="h-5 px-1.5">
                    {incidents.length}
                  </Badge>
                )}
              </TabsTrigger>
              {/* Risks tab hidden — uncomment to re-enable
              <TabsTrigger value="risks" className="flex items-center justify-center gap-2">
                <span>Risks</span>
                {!isLoadingFindings && findings && findings.length > 0 && (
                  <Badge variant="destructive" className="h-5 px-1.5">
                    {findings.length}
                  </Badge>
                )}
              </TabsTrigger>
              */}
            </TabsList>

            <TabsContent value="details" className="mt-0 h-[500px] overflow-y-auto">
              <ResourceDetailsContent resource={resource} />
            </TabsContent>

            <TabsContent value="incidents" className="mt-0 h-[500px] overflow-y-auto">
              <IncidentsContent
                incidents={incidents}
                isLoading={isLoadingIncidents}
                error={incidentsError}
                onViewIncident={(id) => navigate(`/incidents/${id}`)}
              />
            </TabsContent>

            {/* Risks content hidden — uncomment to re-enable
            <TabsContent value="risks" className="mt-0 h-[500px] overflow-y-auto">
              <RiskFindingsContent
                findings={findings}
                isLoading={isLoadingFindings}
                error={findingsError}
              />
            </TabsContent>
            */}
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Resource Details Content Component
interface ResourceDetailsContentProps {
  resource: EnhancedResource;
}

function ResourceDetailsContent({ resource }: ResourceDetailsContentProps) {
  const renderDataPoint = (label: string, value: string | undefined | null) => {
    if (!value || value === 'N/A') return null;
    return (
      <div className="grid grid-cols-[200px,1fr] gap-x-4 py-3 border-b last:border-b-0">
        <span className="font-semibold text-sm text-muted-foreground">{label}</span>
        <span className="text-sm break-all">{value}</span>
      </div>
    );
  };

  const labelEntries = Object.entries(resource.labels || {});

  return (
    <div className="space-y-4 py-4">
      {/* Resource Details Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold">Resource Information</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-0">
            {renderDataPoint("Name", resource.name)}
            {renderDataPoint("Type", resource.kind || resource.type)}
            {renderDataPoint("Namespace", resource.namespace)}
            {renderDataPoint("Cluster", resource.cluster_name)}
            {resource.type === 'cluster' && renderDataPoint("Connected",
              getConnectionLabel(resolveStatus(resource.connection_status, resource.is_connected))
            )}
            {resource.type === 'service' && renderDataPoint("Service Type", resource.service_type)}
            {resource.type === 'service' && renderDataPoint("Cluster IP", resource.cluster_ip)}
            {renderDataPoint("Created At", resource.created_at ? new Date(resource.created_at * 1000).toLocaleString() : 'N/A')}
          </div>
        </CardContent>
      </Card>

      {/* Labels Card */}
      {labelEntries.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold">Labels</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="max-h-72 overflow-y-auto">
              <div className="space-y-0">
                {labelEntries.map(([key, value]) => (
                  <div key={key} className="grid grid-cols-[300px,1fr] gap-x-4 py-3 border-b last:border-b-0">
                    <span className="font-semibold text-sm text-muted-foreground break-all" title={key}>{key}</span>
                    <span className="text-sm break-all" title={String(value)}>{String(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Risk Findings Content Component
interface RiskFindingsContentProps {
  findings: TopologyRiskFinding[];
  isLoading: boolean;
  error: string | null;
}

function RiskFindingsContent({ findings, isLoading, error }: RiskFindingsContentProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!findings || findings.length === 0) {
    return (
      <div className="text-center py-12">

        <h3 className="mt-2 text-sm font-medium text-foreground">No risk findings</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          No security risks have been identified for this resource in the latest assessment.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 py-4">
      <div className="text-sm text-muted-foreground mb-4">
        Found {findings.length} risk{findings.length !== 1 ? 's' : ''} affecting this resource
      </div>
      <div className="space-y-4">
        {findings.map((finding) => (
          <Card key={finding.id} className="border-l-4 border-l-destructive">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold leading-none tracking-tight">
                    {finding.title || 'Security Finding'}
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Severity: <Badge variant="destructive" className="text-xs">{finding.severity}</Badge>
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm text-muted-foreground">
                {finding.description || 'No description available'}
              </p>
              {finding.recommendation && (
                <div className="mt-3 p-3 bg-muted/50 rounded-md">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Recommendation:</p>
                  <p className="text-xs">{finding.recommendation}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Incidents Content Component for K8s resources
interface IncidentsContentProps {
  incidents: TopologyIncident[];
  isLoading: boolean;
  error: string | null;
  onViewIncident: (id: string) => void;
}

function IncidentsContent({ incidents, isLoading, error, onViewIncident }: IncidentsContentProps) {
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-foreground text-background border-foreground/50';
      case 'high': return 'bg-foreground/80 text-background border-foreground/40';
      case 'medium': return 'bg-muted text-foreground border-border';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const getBorderColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'border-l-foreground/60';
      case 'high': return 'border-l-foreground/60';
      case 'medium': return 'border-l-foreground/60';
      case 'low': return 'border-l-foreground/60';
      default: return 'border-l-foreground/60';
    }
  };

  // Format text that contains {{code}}...{{/code}} markers into actual code elements
  const formatCodeTags = (text: string) => {
    if (!text) return null;
    const parts = text.split(/(\{{1,2}code\}{1,2}[\s\S]*?\{{1,2}\/code\}{1,2})/gi);
    return parts.map((part, index) => {
      const codeMatch = part.match(/\{{1,2}code\}{1,2}([\s\S]*?)\{{1,2}\/code\}{1,2}/i);
      if (codeMatch) {
        return (
          <code key={index} className="px-1.5 py-0.5 bg-muted rounded text-sm font-mono">
            {codeMatch[1]}
          </code>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!incidents || incidents.length === 0) {
    return (
      <div className="text-center py-12">

        <h3 className="mt-2 text-sm font-medium text-foreground">No active incidents</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          No incidents have been detected for this resource.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 py-4">
      <div className="text-sm text-muted-foreground mb-4">
        Found {incidents.length} active incident{incidents.length !== 1 ? 's' : ''} affecting this resource
      </div>
      <div className="space-y-4">
        {incidents.map((incident) => (
          <Card key={incident.id} className={cn("border-l-4", getBorderColor(incident.severity))}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                    <h4 className="text-sm font-semibold leading-none tracking-tight">
                      {incident.incident_type && incident.incident_type !== 'Unknown' ? incident.incident_type : incident.type}
                    </h4>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <Badge className={cn("text-xs", getSeverityColor(incident.severity))}>
                      {incident.severity.toUpperCase()}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {getStatusLabel(incident.status)}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {incident.root_cause && (
                <p className="text-sm text-muted-foreground mb-3">{formatCodeTags(incident.root_cause)}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Detected: {new Date(incident.detected_at * 1000).toLocaleString()}
              </p>
              <div className="mt-3 pt-3 border-t">
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 h-auto text-xs"
                  onClick={() => onViewIncident(incident.id)}
                >
                  View Full Incident Details
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Cloud Inventory Tab Component
function CloudInventoryTab() {
  const [loading, setLoading] = useState(true);
  const [resources, setResources] = useState<CloudResource[]>([]);
  const [summary, setSummary] = useState<CloudResourcesSummary | null>(null);
  const [ociSummary, setOciSummary] = useState<OCIResourcesSummary | null>(null);
  const [awsConnections, setAwsConnections] = useState<AWSConnection[]>([]);
  const [ociConnections, setOciConnections] = useState<OCIConnection[]>([]);
  const [selectedCloudProvider, setSelectedCloudProvider] = useState<'all' | 'aws' | 'oci'>('all');
  const [selectedAccount, setSelectedAccount] = useState<string>('all');
  const [selectedResourceType, setSelectedResourceType] = useState<string>('all');
  const [selectedRegion, setSelectedRegion] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selectedResource, setSelectedResource] = useState<CloudResource | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Cloud incident correlation state - maps resource ARN to incident info
  const [cloudIncidentMap, setCloudIncidentMap] = useState<Map<string, {
    incidentCount: number;
    highestSeverity: 'critical' | 'high' | 'medium' | 'low';
    hasRecentIncident: boolean;
    latestIncidentAt: number;
  }>>(new Map());

  // Fetch AWS and OCI connections on mount
  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const [awsResponse, ociResponse] = await Promise.all([
          apiClient.getAWSConnectionStatus().catch(() => ({ connections: [] })),
          apiClient.getOCIConnectionStatus().catch(() => ({ connections: [] })),
        ]);
        setAwsConnections(awsResponse.connections || []);
        setOciConnections(ociResponse.connections || []);
      } catch (err) {
        console.error('Failed to fetch cloud connections:', err);
      }
    };
    fetchConnections();
  }, []);

  // Debounce search query for server-side search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1); // Reset to first page on search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Pagination state from server
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Check if there are any cloud connections for the empty state
  const hasAwsConnections = awsConnections.filter(c => c.connection_status === 'connected').length > 0;
  const hasOciConnections = ociConnections.filter(c => c.connection_status === 'connected').length > 0;
  const hasAnyConnections = hasAwsConnections || hasOciConnections;

  // Fetch cloud resources with unified endpoint and server-side pagination
  const fetchCloudResources = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Determine cloud_provider filter based on selection
      // - 'all' -> undefined (server returns all providers)
      // - 'aws' -> 'aws'
      // - 'oci' -> 'oci'
      const cloudProviderFilter = selectedCloudProvider === 'all' ? undefined : selectedCloudProvider;

      // Determine account filter
      const accountId = selectedAccount !== 'all' ? selectedAccount : undefined;
      const resourceType = selectedResourceType !== 'all' ? selectedResourceType : undefined;

      // Fetch resources using unified endpoint with server-side pagination and search
      const regionFilter = selectedRegion !== 'all' ? selectedRegion : undefined;

      const resourcesResponse = await apiClient.getCloudResources({
        cloud_provider: cloudProviderFilter,
        account_id: accountId,
        resource_type: resourceType,
        region: regionFilter,
        page: currentPage,
        page_size: itemsPerPage,
        search: debouncedSearchQuery || undefined,
      });

      // Fetch summary (for display)
      const summaryResponse = await apiClient.getCloudResourcesSummary(accountId).catch(() => null);

      // Update state with server-side pagination
      setResources(resourcesResponse.resources || []);
      setTotalItems(resourcesResponse.total_count || 0);
      setTotalPages(resourcesResponse.total_pages || 1);
      setSummary(summaryResponse);
      setOciSummary(null);
    } catch (err: any) {
      console.error('Failed to fetch cloud resources:', err);
      setError(err.response?.data?.message || 'Failed to fetch cloud resources');
      setResources([]);
      setTotalItems(0);
      setTotalPages(0);
      setSummary(null);
      setOciSummary(null);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, selectedResourceType, selectedRegion, selectedCloudProvider, itemsPerPage, currentPage, debouncedSearchQuery]);

  useEffect(() => {
    fetchCloudResources();
  }, [fetchCloudResources]);

  // Fetch cloud incidents for correlation with resources
  useEffect(() => {
    const fetchCloudIncidents = async () => {
      if (!hasAnyConnections) {
        setCloudIncidentMap(new Map());
        return;
      }

      try {
        const threeHoursAgo = Math.floor(Date.now() / 1000) - (3 * 60 * 60);
        const now = Math.floor(Date.now() / 1000);

        // Fetch recent cloud incidents
        const response = await apiClient.listCloudIncidents(
          undefined, // all connections
          undefined, // all accounts
          undefined, // all statuses
          1000,      // large page size
          undefined,
          threeHoursAgo,
          now,
          true       // hide transient
        );

        // Build correlation map by ARN/resource_id
        const newMap = new Map<string, {
          incidentCount: number;
          highestSeverity: 'critical' | 'high' | 'medium' | 'low';
          hasRecentIncident: boolean;
          latestIncidentAt: number;
        }>();

        for (const incident of response.incidents || []) {
          // Skip resolved incidents
          if (incident.status === 'resolved_confirmed') continue;

          for (const resource of incident.affected_resources || []) {
            // Use ARN if available (globally unique), otherwise use composite key
            // with connection ID to avoid cross-account collisions
            let key: string;
            if (resource.arn) {
              key = resource.arn;
            } else if (resource.resource_id) {
              // Build composite key: connectionId|resourceId to scope by account (AWS or OCI)
              const connectionId = incident.aws_connection_id || incident.aws_account_id ||
                                  incident.oci_connection_id || '';
              key = connectionId ? `${connectionId}|${resource.resource_id}` : resource.resource_id;
            } else {
              continue;
            }

            const isRecent = incident.detected_at >= threeHoursAgo;
            const existing = newMap.get(key);

            if (!existing) {
              newMap.set(key, {
                incidentCount: 1,
                highestSeverity: incident.severity as 'critical' | 'high' | 'medium' | 'low',
                hasRecentIncident: isRecent,
                latestIncidentAt: incident.detected_at
              });
            } else {
              existing.incidentCount++;
              if (isRecent) existing.hasRecentIncident = true;
              if (incident.detected_at > existing.latestIncidentAt) {
                existing.latestIncidentAt = incident.detected_at;
              }
              // Update severity if higher
              const severityOrder = ['low', 'medium', 'high', 'critical'];
              if (severityOrder.indexOf(incident.severity) > severityOrder.indexOf(existing.highestSeverity)) {
                existing.highestSeverity = incident.severity as 'critical' | 'high' | 'medium' | 'low';
              }
            }
          }
        }

        setCloudIncidentMap(newMap);
      } catch (err) {
        console.error('Failed to fetch cloud incidents for correlation:', err);
      }
    };

    fetchCloudIncidents();
  }, [hasAnyConnections]);

  // Helper to get incident info for a cloud resource
  const getCloudResourceIncidentInfo = (resource: CloudResource) => {
    // Use ARN if available (globally unique), otherwise use composite key
    // with connection ID to match how keys are built in the incident map
    let key: string;
    if (resource.arn) {
      key = resource.arn;
    } else if (resource.resource_id) {
      // Build composite key: connectionId|resourceId to scope by account
      const connectionId = resource.aws_connection_id || resource.aws_account_id ||
                          resource.oci_connection_id || '';
      key = connectionId ? `${connectionId}|${resource.resource_id}` : resource.resource_id;
    } else {
      return null;
    }
    return cloudIncidentMap.get(key) || null;
  };

  // Filter resources by search query
  // Server-side search is applied when debouncedSearchQuery matches searchQuery
  // Client-side filtering provides instant feedback while typing (before debounce completes)
  const filteredResources = resources.filter(resource => {
    // If no search query or server search matches, use server results directly
    if (!searchQuery || searchQuery === debouncedSearchQuery) return true;
    // Client-side filter for instant feedback while debounce is pending
    const query = searchQuery.toLowerCase();
    return (
      (resource.name?.toLowerCase().includes(query)) ||
      resource.resource_id.toLowerCase().includes(query) ||
      resource.resource_type.toLowerCase().includes(query) ||
      (resource.arn?.toLowerCase().includes(query))
    );
  });

  // Reset to page 1 when filters change (but not when page changes)
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedAccount, selectedResourceType, selectedCloudProvider, itemsPerPage]);

  // Reset account selection when cloud provider changes
  useEffect(() => {
    setSelectedAccount('all');
  }, [selectedCloudProvider]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Get unique resource types for filter based on selected cloud provider
  const resourceTypes = (() => {
    const types: { [key: string]: number } = {};

    // Include AWS types if applicable
    if ((selectedCloudProvider === 'all' || selectedCloudProvider === 'aws') && summary?.by_type) {
      Object.entries(summary.by_type).forEach(([type, count]) => {
        types[type] = (types[type] || 0) + count;
      });
    }

    // Include OCI types if applicable
    if ((selectedCloudProvider === 'all' || selectedCloudProvider === 'oci') && ociSummary?.by_type) {
      Object.entries(ociSummary.by_type).forEach(([type, count]) => {
        types[type] = (types[type] || 0) + count;
      });
    }

    return Object.keys(types).sort();
  })();

  // Get unique accounts for filter (AWS and OCI)
  // Deduplicate by account_id since multiple connections can exist per account
  // (e.g., Cloud Ingestion + VPC Flow Logs roles)
  const awsAccounts = Array.from(
    new Map(
      awsConnections
        .filter(conn => conn.connection_status === 'connected')
        .map(conn => [conn.account_id, {
          id: conn.account_id,
          name: `AWS: ${conn.account_id}`,
          provider: 'aws' as const,
        }])
    ).values()
  );

  // Deduplicate by tenancy_ocid for OCI connections
  const ociAccounts = Array.from(
    new Map(
      ociConnections
        .filter(conn => conn.connection_status === 'connected')
        .map(conn => [conn.tenancy_ocid, {
          id: conn.tenancy_ocid,
          name: `OCI: ${conn.connection_name || conn.tenancy_ocid.substring(0, 30) + '...'}`,
          provider: 'oci' as const,
        }])
    ).values()
  );

  // Filter accounts based on selected cloud provider
  const accounts = selectedCloudProvider === 'all'
    ? [...awsAccounts, ...ociAccounts]
    : selectedCloudProvider === 'aws'
    ? awsAccounts
    : ociAccounts;

  // Get unique regions for filter, sorted alphabetically
  const regions = (() => {
    if (!summary?.by_region) return [];
    return Object.entries(summary.by_region)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([region, count]) => ({ region, count }));
  })();

  const getCloudResourceIcon = (resourceType: string) => {
    const type = resourceType.toLowerCase();
    // AWS resource types
    if (type.includes('iam::user')) return <Users className="h-4 w-4 text-purple-600" />;
    if (type.includes('iam::role')) return <Key className="h-4 w-4 text-purple-600" />;
    if (type.includes('iam::policy')) return <Lock className="h-4 w-4 text-purple-600" />;
    if (type.includes('ec2::instance')) return <Server className="h-4 w-4 text-orange-600" />;
    if (type.includes('ec2::vpc')) return <Network className="h-4 w-4 text-blue-600" />;
    if (type.includes('ec2::subnet')) return <Network className="h-4 w-4 text-blue-500" />;
    if (type.includes('ec2::securitygroup')) return <Shield className="h-4 w-4 text-green-600" />;
    if (type.includes('s3::bucket')) return <Database className="h-4 w-4 text-green-600" />;
    if (type.includes('eks::cluster')) return <Boxes className="h-4 w-4 text-blue-600" />;
    if (type.includes('ecs')) return <Boxes className="h-4 w-4 text-orange-500" />;
    if (type.includes('rds')) return <Database className="h-4 w-4 text-blue-600" />;
    if (type.includes('lambda')) return <HardDrive className="h-4 w-4 text-yellow-600" />;
    // OCI resource types (red/oracle color)
    // Types use format: OCI::Core::VCN, OCI::Compute::Instance, etc.
    if (type.includes('oci::compute::instance')) return <Server className="h-4 w-4 text-red-600" />;
    if (type.includes('oci::core::vcn')) return <Network className="h-4 w-4 text-red-600" />;
    if (type.includes('oci::core::subnet')) return <Network className="h-4 w-4 text-red-500" />;
    if (type.includes('oci::core::internetgateway')) return <Network className="h-4 w-4 text-red-500" />;
    if (type.includes('oci::core::natgateway')) return <Network className="h-4 w-4 text-red-500" />;
    if (type.includes('oci::core::servicegateway')) return <Network className="h-4 w-4 text-red-500" />;
    if (type.includes('oci::database')) return <Database className="h-4 w-4 text-red-600" />;
    if (type.includes('oci::objectstorage')) return <HardDrive className="h-4 w-4 text-red-600" />;
    if (type.includes('oci::blockstorage')) return <HardDrive className="h-4 w-4 text-red-600" />;
    if (type.includes('oci::container')) return <Boxes className="h-4 w-4 text-red-600" />;
    if (type.includes('oci::containerengine')) return <Boxes className="h-4 w-4 text-red-600" />;
    if (type.includes('oci::loadbalancer')) return <Network className="h-4 w-4 text-red-500" />;
    if (type.includes('oci::identity')) return <Users className="h-4 w-4 text-red-600" />;
    if (type.includes('oci::')) return <Cloud className="h-4 w-4 text-red-600" />;
    return <Cloud className="h-4 w-4 text-gray-600" />;
  };

  const formatResourceType = (type: string) => {
    const displayNames: Record<string, string> = {
      // AWS - Compute
      'AWS::EC2::Instance': 'EC2 Instance',
      'AWS::ECS::Cluster': 'ECS Cluster',
      'AWS::ECS::Service': 'ECS Service',
      'AWS::EKS::Cluster': 'EKS Cluster',
      'AWS::Lambda::Function': 'Lambda Function',
      // AWS - Network
      'AWS::EC2::VPC': 'VPC',
      'AWS::EC2::Subnet': 'Subnet',
      'AWS::EC2::SecurityGroup': 'Security Group',
      'AWS::EC2::NatGateway': 'NAT Gateway',
      'AWS::EC2::VPCEndpoint': 'VPC Endpoint',
      'AWS::ElasticLoadBalancingV2::LoadBalancer': 'Load Balancer',
      // AWS - IAM
      'AWS::IAM::User': 'IAM User',
      'AWS::IAM::Role': 'IAM Role',
      'AWS::IAM::Policy': 'IAM Policy',
      // AWS - Storage & Database
      'AWS::S3::Bucket': 'S3 Bucket',
      'AWS::RDS::DBInstance': 'RDS Instance',
      'AWS::DynamoDB::Table': 'DynamoDB Table',
      // OCI - Compute
      'OCI::Compute::Instance': 'Compute Instance',
      'OCI::ContainerEngine::Cluster': 'OKE Cluster',
      'OCI::ContainerInstances::ContainerInstance': 'Container Instance',
      // OCI - Network
      'OCI::Core::VCN': 'VCN',
      'OCI::Core::Subnet': 'Subnet',
      'OCI::Core::SecurityList': 'Security List',
      'OCI::Core::NetworkSecurityGroup': 'Network Security Group',
      'OCI::Core::InternetGateway': 'Internet Gateway',
      'OCI::Core::NATGateway': 'NAT Gateway',
      'OCI::Core::ServiceGateway': 'Service Gateway',
      'OCI::LoadBalancer::LoadBalancer': 'Load Balancer',
      // OCI - IAM
      'OCI::Identity::User': 'User',
      'OCI::Identity::Group': 'Group',
      // OCI - Storage & Database
      'OCI::ObjectStorage::Bucket': 'Object Storage Bucket',
      'OCI::Database::AutonomousDatabase': 'Autonomous Database',
      'OCI::NoSQL::Table': 'NoSQL Table',
      // OCI - Serverless
      'OCI::Functions::Application': 'Functions Application',
      'OCI::Functions::Function': 'Function',
    };
    if (displayNames[type]) return displayNames[type];
    // Fallback: strip provider prefix and join remaining parts
    const parts = type.split('::');
    if (parts.length >= 3) {
      return `${parts[1]} ${parts[2]}`;
    }
    return type;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // No cloud connections - show setup prompt
  if (!loading && !hasAnyConnections) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <h3 className="text-lg font-semibold">No Cloud Connections</h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
              Connect your AWS or OCI account to start ingesting cloud resources.
              Go to Cloud Integrations to set up a connection with Cloud Resource Inventory.
            </p>
            <Button
              className="mt-4"
              onClick={() => window.location.href = '/cloud-integrations'}
            >
              Set Up Cloud Connection
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Cloud Resource Inventory</CardTitle>
            <Button onClick={fetchCloudResources} variant="outline" disabled={loading}>
              <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary Cards */}
          {(summary || ociSummary || hasAnyConnections) && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">{(summary?.total || 0) + (ociSummary?.total || 0)}</div>
                  <p className="text-xs text-muted-foreground">Total Resources</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">
                    {Object.keys({ ...(summary?.by_type || {}), ...(ociSummary?.by_type || {}) }).length}
                  </div>
                  <p className="text-xs text-muted-foreground">Resource Types</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">{awsAccounts.length}</div>
                  <p className="text-xs text-muted-foreground">AWS Accounts</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">{ociAccounts.length}</div>
                  <p className="text-xs text-muted-foreground">OCI Tenancies</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search by name, ID, or ARN..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <Select value={selectedCloudProvider} onValueChange={(v) => setSelectedCloudProvider(v as 'all' | 'aws' | 'oci')}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Cloud Provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clouds</SelectItem>
                <SelectItem value="aws">AWS</SelectItem>
                <SelectItem value="oci">OCI</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedResourceType} onValueChange={setSelectedResourceType}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Resource Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Resource Types</SelectItem>
                {resourceTypes.map(type => {
                  // Calculate count from both AWS and OCI summaries based on selected provider
                  const awsCount = (selectedCloudProvider === 'all' || selectedCloudProvider === 'aws')
                    ? (summary?.by_type?.[type] || 0)
                    : 0;
                  const ociCount = (selectedCloudProvider === 'all' || selectedCloudProvider === 'oci')
                    ? (ociSummary?.by_type?.[type] || 0)
                    : 0;
                  const totalCount = awsCount + ociCount;
                  return (
                    <SelectItem key={type} value={type}>
                      {formatResourceType(type)} ({totalCount})
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            <Select value={selectedAccount} onValueChange={setSelectedAccount}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Account / Tenancy" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accounts</SelectItem>
                {accounts.map(account => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedRegion} onValueChange={(v) => { setSelectedRegion(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Region" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                {regions.map(({ region, count }) => (
                  <SelectItem key={region} value={region}>
                    {region} ({count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={itemsPerPage.toString()} onValueChange={(value) => {
              setItemsPerPage(parseInt(value));
              setCurrentPage(1);
            }}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="20">20 per page</SelectItem>
                <SelectItem value="50">50 per page</SelectItem>
                <SelectItem value="100">100 per page</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Results Summary */}
          {!loading && !error && (
            <div className="text-sm text-muted-foreground">
              Showing {filteredResources.length} of {totalItems} resources
              {debouncedSearchQuery && ` matching "${debouncedSearchQuery}"`}
              {totalPages > 1 && ` - Page ${currentPage} of ${totalPages}`}
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Name / ID</TableHead>
                    <TableHead>Cloud</TableHead>
                    <TableHead>Region</TableHead>
                    <TableHead>Account / Tenancy</TableHead>
                    <TableHead>Last Seen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredResources.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        {error ? 'Failed to load resources' : 'No cloud resources found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredResources.map((resource) => {
                      const isOci = resource.resource_type.startsWith('OCI::');
                      const incidentInfo = getCloudResourceIncidentInfo(resource);
                      return (
                        <TableRow
                          key={resource.id}
                          className={cn(
                            "cursor-pointer hover:bg-muted/50",
                            incidentInfo?.hasRecentIncident && "animate-incident-pulse"
                          )}
                          title={incidentInfo?.hasRecentIncident
                            ? `⚠️ Active incident detected in the last 4 hours (${incidentInfo.incidentCount} incident${incidentInfo.incidentCount !== 1 ? 's' : ''}, ${incidentInfo.highestSeverity} severity). Click to view details.`
                            : undefined
                          }
                          onClick={() => {
                            setSelectedResource(resource);
                            setDetailsOpen(true);
                          }}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="text-sm">
                                {formatResourceType(resource.resource_type)}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2">
                                <span>{resource.name || resource.resource_id}</span>
                                {incidentInfo?.hasRecentIncident && (
                                  <span
                                    className="relative flex h-2.5 w-2.5"
                                    title={`${incidentInfo.incidentCount} active incident(s) - ${incidentInfo.highestSeverity} severity`}
                                  >
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                                  </span>
                                )}
                              </div>
                              {resource.name && (
                                <span className="text-xs text-muted-foreground font-mono">
                                  {resource.resource_id}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {isOci ? (
                              <img src={ociLogo} alt="OCI" className="h-5 w-auto" />
                            ) : (
                              <>
                                <img src={awsLogoLight} alt="AWS" className="h-5 w-auto dark:hidden" />
                                <img src={awsLogoDark} alt="AWS" className="h-5 w-auto hidden dark:block" />
                              </>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{resource.region}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {isOci
                              ? (resource.oci_tenancy_ocid?.substring(0, 20) + '...')
                              : resource.aws_account_id}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(resource.last_seen_at)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <div className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>
              {totalPages > 1 && (
                <div className="flex flex-wrap gap-2 items-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>

                  {/* Page numbers - hidden on mobile to prevent overflow */}
                  <div className="hidden sm:flex gap-1">
                    {currentPage > 2 && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(1)}
                        >
                          1
                        </Button>
                        {currentPage > 3 && <span className="px-2 py-1">...</span>}
                      </>
                    )}

                    {currentPage > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        {currentPage - 1}
                      </Button>
                    )}

                    <Button size="sm" disabled className="bg-foreground text-background hover:bg-foreground disabled:opacity-100">
                      {currentPage}
                    </Button>

                    {currentPage < totalPages && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        {currentPage + 1}
                      </Button>
                    )}

                    {currentPage < totalPages - 1 && (
                      <>
                        {currentPage < totalPages - 2 && <span className="px-2 py-1">...</span>}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(totalPages)}
                        >
                          {totalPages}
                        </Button>
                      </>
                    )}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cloud Resource Details Dialog */}
      <CloudResourceDetailsDialog
        resource={selectedResource}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
      />
    </>
  );
}

// Cloud Resource Details Dialog Component
interface CloudResourceDetailsDialogProps {
  resource: CloudResource | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function CloudResourceDetailsDialog({ resource, open, onOpenChange }: CloudResourceDetailsDialogProps) {
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState<CloudIncidentSummary[]>([]);
  const [isLoadingIncidents, setIsLoadingIncidents] = useState(false);
  const [incidentsError, setIncidentsError] = useState<string | null>(null);

  // Fetch cloud incidents for this resource when dialog opens
  useEffect(() => {
    if (!resource || !open) {
      setIncidents([]);
      setIncidentsError(null);
      return;
    }

    const fetchIncidents = async () => {
      setIsLoadingIncidents(true);
      setIncidentsError(null);

      try {
        // Use the efficient backend endpoint that filters by resource
        // Pass cloud provider-specific parameters based on resource type
        const response = await apiClient.getCloudIncidentsByResource({
          arn: resource.arn,
          resourceId: resource.resource_id,
          cloudProvider: resource.cloud_provider,
          // AWS-specific
          awsConnectionId: resource.aws_connection_id,
          awsAccountId: resource.aws_account_id,
          // OCI-specific (for future OCI incident support)
          ociConnectionId: resource.oci_connection_id,
          ociTenancyOcid: resource.oci_tenancy_ocid
        });

        setIncidents(response.incidents || []);
      } catch (error: any) {
        console.error('Error fetching cloud incidents:', error);
        setIncidentsError('Failed to load incidents');
      } finally {
        setIsLoadingIncidents(false);
      }
    };

    fetchIncidents();
  }, [resource, open]);

  if (!resource) return null;

  const renderDataPoint = (label: string, value: string | undefined | null) => {
    if (!value) return null;
    return (
      <div className="grid grid-cols-[200px,1fr] gap-x-4 py-3 border-b last:border-b-0">
        <span className="font-semibold text-sm text-muted-foreground">{label}</span>
        <span className="text-sm break-all font-mono">{value}</span>
      </div>
    );
  };

  const formatResourceTypeLocal = (type: string) => {
    const parts = type.split('::');
    if (parts.length >= 3) {
      return `${parts[1]} ${parts[2]}`;
    }
    return type;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[650px] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cloud className="h-5 w-5" />
            {formatResourceTypeLocal(resource.resource_type)}: {resource.name || resource.resource_id}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-hidden">
          <Tabs defaultValue="details" className="w-full h-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="incidents" className="flex items-center justify-center gap-2">
                <span>Incidents</span>
                {!isLoadingIncidents && incidents.length > 0 && (
                  <Badge variant="destructive" className="h-5 px-1.5">
                    {incidents.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="mt-0 h-[500px] overflow-y-auto">
              <div className="space-y-4 py-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg font-semibold">Resource Information</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-0">
                      {renderDataPoint("Name", resource.name)}
                      {renderDataPoint("Resource ID", resource.resource_id)}
                      {renderDataPoint("Type", resource.resource_type)}
                      {renderDataPoint("ARN", resource.arn)}
                      {renderDataPoint("Region", resource.region)}
                      {resource.aws_account_id && renderDataPoint("AWS Account", resource.aws_account_id)}
                      {resource.oci_tenancy_ocid && renderDataPoint("OCI Tenancy", resource.oci_tenancy_ocid)}
                      {renderDataPoint("Status", resource.resource_status)}
                      {renderDataPoint("First Seen", new Date(resource.first_seen_at).toLocaleString())}
                      {renderDataPoint("Last Seen", new Date(resource.last_seen_at).toLocaleString())}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="incidents" className="mt-0 h-[500px] overflow-y-auto">
              <CloudIncidentsContent
                incidents={incidents}
                isLoading={isLoadingIncidents}
                error={incidentsError}
                onViewIncident={(id) => navigate(`/cloud-incidents/${id}`)}
              />
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Cloud Incidents Content Component
interface CloudIncidentsContentProps {
  incidents: CloudIncidentSummary[];
  isLoading: boolean;
  error: string | null;
  onViewIncident: (id: string) => void;
}

function CloudIncidentsContent({ incidents, isLoading, error, onViewIncident }: CloudIncidentsContentProps) {
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-foreground text-background border-foreground/50';
      case 'high': return 'bg-foreground/80 text-background border-foreground/40';
      case 'medium': return 'bg-muted text-foreground border-border';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const getBorderColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'border-l-foreground/60';
      case 'high': return 'border-l-foreground/60';
      case 'medium': return 'border-l-foreground/60';
      case 'low': return 'border-l-foreground/60';
      default: return 'border-l-foreground/60';
    }
  };

  // Format text that contains {{code}}...{{/code}} markers into actual code elements
  const formatCodeTags = (text: string) => {
    if (!text) return null;
    const parts = text.split(/(\{{1,2}code\}{1,2}[\s\S]*?\{{1,2}\/code\}{1,2})/gi);
    return parts.map((part, index) => {
      const codeMatch = part.match(/\{{1,2}code\}{1,2}([\s\S]*?)\{{1,2}\/code\}{1,2}/i);
      if (codeMatch) {
        return (
          <code key={index} className="px-1.5 py-0.5 bg-muted rounded text-sm font-mono">
            {codeMatch[1]}
          </code>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!incidents || incidents.length === 0) {
    return (
      <div className="text-center py-12">

        <h3 className="mt-2 text-sm font-medium text-foreground">No incidents</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          No incidents have been detected for this resource.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 py-4">
      <div className="text-sm text-muted-foreground mb-4">
        Found {incidents.length} incident{incidents.length !== 1 ? 's' : ''} affecting this resource (all time)
      </div>
      <div className="space-y-4">
        {incidents.map((incident) => (
          <Card key={incident.id} className={cn("border-l-4", getBorderColor(incident.severity))}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                    <h4 className="text-sm font-semibold leading-none tracking-tight">
                      {incident.type}
                    </h4>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <Badge className={cn("text-xs", getSeverityColor(incident.severity))}>
                      {incident.severity.toUpperCase()}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {getStatusLabel(incident.status)}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {incident.rca_result?.root_cause && (
                <p className="text-sm text-muted-foreground mb-3">{formatCodeTags(incident.rca_result.root_cause)}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Detected: {new Date(incident.detected_at * 1000).toLocaleString()}
              </p>
              <div className="mt-3 pt-3 border-t">
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 h-auto text-xs"
                  onClick={() => onViewIncident(incident.id)}
                >
                  View Full Incident Details
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}